<script setup lang="ts">
import { computed, nextTick, shallowRef, watch } from 'vue'
import { useRouter } from 'vue-router'

import MapControls from '@/components/map/MapControls.vue'
import type { MapMode } from '@/components/map/mapMode'
import MapView from '@/components/map/MapView.vue'
import StopDetailsDialog from '@/components/transit/StopDetailsDialog.vue'
import StopRow from '@/components/transit/StopRow.vue'
import { useMapControls } from '@/composables/useMapControls'
import { useScrollRestore } from '@/composables/useScrollRestore'
import { routeCodesAtStop, useTransitIndex } from '@/composables/useTransitIndex'
import type { IndexedStop } from '@/composables/useTransitIndex'
import { compareRouteCodes } from '@/utils/routeOrder'
import { matchesNormalized, normalizeForSearch } from '@/utils/textSearch'

import LookupScreenShell from './LookupScreenShell.vue'

/** Rows rendered initially, and the amount added each time the list nears its end. */
const PAGE_SIZE = 50

const router = useRouter()
const { index, isPending } = useTransitIndex()
const {
  basemap,
  toggleBasemap,
  location: userLocation,
  isLocating,
  error: locationError,
  locate,
} = useMapControls()

const query = shallowRef('')
const windowSize = shallowRef(PAGE_SIZE)
const selectedStopId = shallowRef<number>()
const dialogOpen = shallowRef(false)
const dialogStop = shallowRef<IndexedStop>()

const matching = computed<IndexedStop[]>(() => {
  const all = index.value?.allStops ?? []
  const term = normalizeForSearch(query.value)
  if (!term) return all
  return all.filter((stop) => matchesNormalized(stop.normalizedName, term))
})

/**
 * 4,991 rows cannot be mounted. An incremental window beats a virtual scroller
 * here: no new dependency, no measurement pass, and in practice the list is
 * filtered by search long before anyone scrolls to the end.
 */
const visible = computed(() => matching.value.slice(0, windowSize.value))

// Reset on every query change, or a search would appear to return more rows
// than it has — the window would carry over from the previous scroll position.
watch(query, () => {
  windowSize.value = PAGE_SIZE
  selectedStopId.value = undefined
})

/**
 * Both halves of the saved state are load-bearing. The offset alone would be
 * restored into a list that has reset to unfiltered and to its first 50 rows, so
 * an offset measured at row 300 would clamp to the bottom of a 50-row list.
 *
 * The assignment order is not incidental: the `watch(query, …)` above resets
 * `windowSize` to `PAGE_SIZE` whenever the query changes. Setting `query` first
 * and awaiting a tick lets that reset fire and finish, so widening the window
 * afterwards survives instead of being immediately undone.
 */
const { containerRef } = useScrollRestore({
  id: 'lookup-stops',
  capture: () => ({ query: query.value, windowSize: windowSize.value }),
  restore: async (saved) => {
    query.value = saved.query
    await nextTick()
    windowSize.value = saved.windowSize
  },
})

function onScroll(event: Event) {
  const el = event.target as HTMLElement
  const nearEnd = el.scrollTop + el.clientHeight >= el.scrollHeight - 200
  if (nearEnd && windowSize.value < matching.value.length) {
    windowSize.value += PAGE_SIZE
  }
}

function codesFor(stopId: number) {
  const entries = index.value?.routesByStopId.get(stopId)
  return entries ? [...routeCodesAtStop(entries)].sort(compareRouteCodes) : []
}

function selectStop(stopId: number) {
  if (selectedStopId.value === stopId) {
    dialogStop.value = index.value?.stopsById.get(stopId)
    dialogOpen.value = Boolean(dialogStop.value)
    return
  }
  selectedStopId.value = stopId
}

/** The map shows what the list currently shows, so searching narrows both. */
const mode = computed<MapMode>(() => ({
  kind: 'stops',
  stops: visible.value,
  selectedStopId: selectedStopId.value,
  showSelectedStopPopup: false,
}))

const dialogRouteCodes = computed(() => (dialogStop.value ? codesFor(dialogStop.value.id) : []))

function inspectStop(stopId: number) {
  const stop = index.value?.stopsById.get(stopId)
  if (!stop) return
  dialogStop.value = stop
  dialogOpen.value = true
}

function openStop(stopId: number) {
  dialogOpen.value = false
  router.push({ name: 'stop-buses', params: { stopId: String(stopId) } })
}
</script>

<template>
  <LookupScreenShell v-model="query">
    <!-- Split view from look_up_detail_bus_stop.jpg: list above, map below.
         Both panes are height-constrained so the list scrolls internally
         instead of pushing the map off the screen. -->
    <div class="flex min-h-0 flex-1 flex-col">
      <div
        ref="containerRef"
        class="min-h-0 flex-55 overflow-y-auto overscroll-contain"
        @scroll="onScroll"
      >
        <p v-if="isPending" class="p-4 text-xs text-muted-foreground">Loading network data…</p>

        <template v-else-if="visible.length > 0">
          <StopRow
            v-for="stop in visible"
            :key="stop.id"
            :name="stop.name"
            :route-codes="codesFor(stop.id)"
            :selected="stop.id === selectedStopId"
            @select="selectStop(stop.id)"
          />
        </template>

        <p v-else class="p-4 text-sm text-muted-foreground">No stop matches “{{ query }}”.</p>
      </div>

      <StopDetailsDialog
        v-model:open="dialogOpen"
        :stop="dialogStop"
        :route-codes="dialogRouteCodes"
        @view-buses="openStop"
      />

      <div class="relative min-h-0 flex-45 border-t">
        <MapView
          :mode="mode"
          :basemap="basemap"
          :user-location="userLocation"
          @select-stop="inspectStop"
          @view-stop-details="openStop"
        />

        <!-- The same stack as every other map: wherever there is a map, there is
             a way back to where you are and a way to see the imagery. -->
        <MapControls
          :basemap="basemap"
          :is-locating="isLocating"
          :error="locationError"
          @toggle-basemap="toggleBasemap"
          @locate="locate"
        />
      </div>
    </div>
  </LookupScreenShell>
</template>
